import React, { createContext, useContext, ReactNode } from 'react';

export interface AuthUser {
  token: string;
  userId: string;
  tenantId: string;
  role: 'admin' | 'analyst' | 'member_services';
  email: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  login: () => {},
  logout: () => {},
});

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

function parseJwt(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const parsed = parseJwt(token);
  if (!parsed || typeof parsed['exp'] !== 'number') return true;
  return Date.now() / 1000 > (parsed['exp'] as number);
}

function buildAuthUser(token: string): AuthUser | null {
  const parsed = parseJwt(token);
  if (!parsed) return null;
  return {
    token,
    userId: parsed['sub'] as string,
    tenantId: parsed['tenantId'] as string,
    role: parsed['role'] as AuthUser['role'],
    email: parsed['email'] as string,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const storedToken = localStorage.getItem('access_token');
  const initialUser =
    storedToken && !isTokenExpired(storedToken) ? buildAuthUser(storedToken) : null;

  const [user, setUser] = React.useState<AuthUser | null>(initialUser);

  const login = React.useCallback((token: string) => {
    localStorage.setItem('access_token', token);
    setUser(buildAuthUser(token));
  }, []);

  const logout = React.useCallback(() => {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');

    // Best-effort real server-side revocation (POST /api/auth/logout) —
    // local state must still clear even if this fails (network down, token
    // already expired), so this never blocks or throws into the caller.
    if (accessToken) {
      fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(refreshToken ? { refreshToken } : {}),
      }).catch(() => { /* local logout still proceeds below */ });
    }

    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    // Clear CU-switch context so next login starts fresh on the platform view
    sessionStorage.removeItem('platform_token');
    sessionStorage.removeItem('switched_cu_name');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
